import { apiRequest } from '@/utils/api-request';
import * as Sentry from '@sentry/react';

// The BE runs a Temporal Update, not a Signal, so this reflects what the
// workflow decided rather than just that the RPC was accepted.
export interface DeliverClarificationResponse {
  success: boolean;
  message: string;
  session_id: string;
  node_id: string;
  /**
   * True when the answer arrived after the clarification had already
   * resolved/timed out: FE should treat it as logged-but-ignored and
   * offer a rerun-with-answer affordance.
   */
  was_already_resolved?: boolean;
  /**
   * True when the workflow truncated the answer to the 8000-char cap.
   * Always false via the FE (the request schema enforces the cap);
   * forwarded verbatim so direct-SDK / CLI callers can detect tail-loss.
   */
  truncated?: boolean;
  detail?: string;
}

/**
 * Deliver a user's answer to a node's clarifying question.
 *
 * session_id must match the run that emitted the event or the 403 IDOR guard
 * fires. Deliberately unretried: the BE Update is idempotent, so the caller
 * notifies and lets the user resubmit.
 */
export const deliverClarification = async (
  workflowId: string,
  sessionId: string,
  orgId: string,
  nodeId: string,
  answer: string,
  // Skip: the BE ignores answer and resolves as an expired countdown would.
  declined = false,
): Promise<DeliverClarificationResponse> => {
  try {
    // ``apiRequest`` does NOT throw on HTTP errors — it resolves to
    // ``{ detail, success: false }`` on non-2xx and ``{ data: <body> }``
    // on success. A naive cast would let a 403/410/500 flow through as
    // success, optimistically removing the pending card. Unwrap
    // explicitly and throw so the editor's catch (re-insert + notify) runs.
    const response = await apiRequest<DeliverClarificationResponse>({
      url: `/api/v1/orgs/${orgId}/workflows/${workflowId}/runs/session/${sessionId}/deliver-clarification`,
      method: 'POST',
      target: 'workflow',
      body: { node_id: nodeId, answer, declined },
    });
    if (response?.detail) {
      throw new Error(response.detail);
    }
    // Defensive ``response.data ?? response`` (matches
    // ``get-run-by-session.ts``): resilient if a future route returns
    // the body flat instead of wrapped.
    const data = (response?.data ?? response) as
      | DeliverClarificationResponse
      | undefined;
    if (!data || data.success === false) {
      // BE reported failure inside a 2xx envelope (or shape was
      // unrecognisable); throw so the optimistic UI rolls back.
      throw new Error(
        data?.detail ||
          data?.message ||
          'Clarification answer was not accepted by the backend',
      );
    }
    return data;
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        context: 'deliver-clarification',
        workflowId,
        sessionId,
        nodeId,
        answerLen: answer.length,
      },
    });
    throw error;
  }
};
